import { useEffect, useState } from 'react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Loader2 } from 'lucide-react'
import Modal from '../components/Modal'
import { useAppStore } from '../store'
import { v4 as uuidv4 } from 'uuid'
import { JobStatus, JobEnum, JobEnumType, configSchema, JobCompatibility } from '@/types/job'
import { Textarea } from '@/components/ui/textarea'
import SearchableSelect from '@/components/SearcheableSelect'
import KeyValueEditor, {
  fromRecordToEntry,
  fromEntryToRecord,
  validateEntries,
} from '@/components/KeyValueEditor'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTranslation } from '@/i18n'

type JobCreateModalProps = {
  isOpen: boolean
  onClose: () => void
}


const JobCreateModal = ({ isOpen, onClose }: JobCreateModalProps) => {
  const { t } = useTranslation()
  const { createJob, fetchJobs, fetchConnectors, connectors, user } =
    useAppStore()

  // Form state
  const [jobName, setJobName] = useState('')
  const [jobDescription, setJobDescription] = useState('')
  const [executorId, setExecutorId] = useState('')
  const [jobType, setJobType] = useState<JobEnumType>(JobEnum.GENERIC)
  const [configEntries, setConfigEntries] = useState(
    fromRecordToEntry({}, configSchema[JobEnum.GENERIC])
  )
  const [loadingExecutors, setLoadingExecutors] = useState(false)
  const [errors, setErrors] = useState<{
    jobName?: string
    executorId?: string
    jobType?: string
    showJobName: boolean
    showExecutor: boolean
    showJobType: boolean
  }>({ showJobName: false, showExecutor: false, showJobType: false })
  const [executorOptions, setExecutorOptions] = useState<
    { label: string; value: string }[]
  >([])

  // Load executors when the modal opens
  useEffect(() => {
    if (isOpen && connectors.length === 0) {
      setLoadingExecutors(true)
      fetchConnectors().finally(() => setLoadingExecutors(false))
      // TODO: Filter connector list with job type compatibility
    }
  }, [isOpen, fetchConnectors, connectors.length])

  // Convert the list of `connectors` into options for the `SearchableSelect`
  useEffect(() => {
    const compatibleConnectors = connectors.filter((connector) =>
      JobCompatibility[jobType].includes(connector.type)
    )
    setExecutorOptions(
      compatibleConnectors.map((connector) => ({
        label: connector.name,
        value: connector.id,
      }))
    )
  }, [connectors, jobType])

  // Update config entries when job type changes
  useEffect(() => {
    setConfigEntries(fromRecordToEntry({}, configSchema[jobType]))
  }, [jobType])

  // Validation before submitting the form
  const validate = () => {
    let hasErrors = false

    // Validate jobName, executorId, and jobType
    setErrors(() => {
      // TODO: Replace this with Tanstack
      const newErrors = {
        jobName: !jobName.trim() ? t('forms:errors.job_name_required') : undefined,
        executorId: !executorId.trim() ? t('forms:errors.executor_id_required') : undefined,
        jobType: !jobType ? t('forms:errors.job_type_required') : undefined,
        showJobName: true,
        showExecutor: true,
        showJobType: true,
      }
      if (newErrors.jobName || newErrors.executorId || newErrors.jobType)
        hasErrors = true
      return newErrors
    })

    const validatedEntries = validateEntries(configEntries, t).map((entry) => ({
      ...entry,
      showError: true,
    }))
    setConfigEntries(validatedEntries)
    return hasErrors || validatedEntries.some((entry) => entry.error)
  }

  // Handle the creation of the Job
  const handleCreateJob = async () => {
    const hasErrors = validate()
    if (hasErrors) return

    const newJob = {
      id: uuidv4(),
      status: JobStatus.PENDING,
      type: jobType,
      executor_id: executorId,
      name: jobName,
      description: jobDescription,
      created_by: user?.userId || 'unknown',
      tenant_id: '', // Will be set by backend from JWT
      config: fromEntryToRecord(configEntries),
      created_at: new Date().toISOString(),
    }

    try {
      await createJob({ job: newJob })
      await fetchJobs()
      onCloseReset()
    } catch (error) {
      console.error(t('jobs:error_creating_job'), error)
    }
  }

  // Reset form state
  const resetForm = () => {
    setJobName('')
    setJobDescription('')
    setExecutorId('')
    setJobType(JobEnum.GENERIC)
    setConfigEntries(fromRecordToEntry({}, configSchema[JobEnum.GENERIC]))
    setErrors({ showJobName: false, showExecutor: false, showJobType: false })
  }

  const onCloseReset = () => {
    onClose()
    resetForm()
  }

  return (
    <Modal isOpen={isOpen} onClose={onCloseReset} title={t('jobs:create_new_job')}>
      <div className="grid gap-4 py-4">
        {/* Job Name */}
        <div className="grid gap-2">
          <Label htmlFor="name">{t('jobs:job_name')}</Label>
          <Input
            id="name"
            value={jobName}
            onChange={(e) => {
              setJobName(e.target.value)
              setErrors((prev) => ({ ...prev, showJobName: false }))
            }}
            placeholder={t('forms:placeholders.enter_job_name')}
          />
          {errors.showJobName && errors.jobName && (
            <p className="text-red-500 text-sm">{errors.jobName}</p>
          )}
        </div>

        {/* Description */}
        <div className="grid gap-2">
          <Label htmlFor="description">{t('forms:labels.description')}</Label>
          <Textarea
            id="description"
            value={jobDescription}
            onChange={(e) => setJobDescription(e.target.value)}
            placeholder={t('forms:placeholders.enter_job_description')}
          />
        </div>

        {/* Job Type */}
        <div className="grid gap-2">
          <Label htmlFor="type">{t('jobs:job_type')}</Label>
          <Select
            value={jobType}
            onValueChange={(value) => {
              setJobType(value as JobEnumType)
              setErrors((prev) => ({ ...prev, showJobType: false }))
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('jobs:job_type')} />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(JobEnum).map(([key, value]) => (
                <SelectItem key={key} value={value}>
                  {value.toUpperCase()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.showJobType && errors.jobType && (
            <p className="text-red-500 text-sm">{errors.jobType}</p>
          )}
        </div>

        {/* Executor ID (with dynamic search) */}
        <div className="grid gap-2">
          <Label htmlFor="executor_id">{t('jobs:executor')}</Label>
          {loadingExecutors ? (
            <div className="flex items-center text-sm text-gray-500 dark:text-dark-text-muted">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              {t('jobs:loading_executors')}
            </div>
          ) : (
            <SearchableSelect
              options={executorOptions}
              value={executorId}
              onChange={(value) => {
                setExecutorId(value)
                setErrors((prev) => ({ ...prev, showExecutor: false }))
              }}
              placeholder={
                executorOptions.length > 0
                  ? t('jobs:select_executor')
                  : t('jobs:no_executors_found')
              }
              disabled={executorOptions.length === 0}
            />
          )}
          {errors.showExecutor && errors.executorId && (
            <p className="text-red-500 text-sm">{errors.executorId}</p>
          )}
        </div>

        {/* Config (KeyValueEditor) */}
        <div className="grid gap-2">
          <Label htmlFor="config">{t('forms:labels.configuration')}</Label>
          <KeyValueEditor
            entries={configEntries}
            onChange={setConfigEntries}
            schema={configSchema[jobType]}
          />
        </div>
      </div>

      {/* Buttons in the modal */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCloseReset}>
          {t('common:buttons.cancel')}
        </Button>
        <Button onClick={handleCreateJob}>{t('common:buttons.create')}</Button>
      </div>
    </Modal>
  )
}

export default JobCreateModal
